import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Activity,
  TrendingUp,
  TrendingDown,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  PieChart,
  Target,
  Zap,
  Brain,
  BarChart3,
  ArrowRight,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  RefreshCw,
  Calendar,
  DollarSign,
  Percent,
  AlertCircle,
  ChevronRight,
  Lightbulb,
  Clock,
  TrendingUp as TrendUp,
  Flame,
  ThumbsUp,
  ThumbsDown,
  Scale,
  Umbrella,
  Gift,
  LineChart
} from 'lucide-react';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import Layout from '../components/Layout';
import { AskAIBar, askAIPath } from '../components/AskAIBar';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortfolioIntelligence() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [intelligence, setIntelligence] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchIntelligence = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portfolio/intelligence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntelligence(response.data);
    } catch (error) {
      console.error('Failed to fetch intelligence:', error);
      // Use mock data for demo
      setIntelligence(getMockIntelligence());
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchIntelligence();
  }, [fetchIntelligence]);

  const refreshIntelligence = async () => {
    setRefreshing(true);
    await fetchIntelligence();
    setRefreshing(false);
    toast.success(t('Intelligence refreshed!', 'Akili imeonyeshwa upya!'));
  };

  // Mock data for demonstration
  const getMockIntelligence = () => ({
    health_score: 72,
    health_grade: 'B+',
    last_updated: new Date().toISOString(),
    
    // Score breakdown
    scores: {
      diversification: { score: 65, max: 100, status: 'warning', message: 'Too concentrated in Banking sector' },
      risk_alignment: { score: 85, max: 100, status: 'good', message: 'Matches your Moderate profile' },
      performance: { score: 78, max: 100, status: 'good', message: 'Outperforming DSE index by 3.2%' },
      dividend_health: { score: 60, max: 100, status: 'warning', message: 'Below average yield' }
    },
    
    // Stress test results
    stress_test: {
      scenario_mild: { name: '10% Market Dip', impact: -8.5, recovery_months: 4 },
      scenario_moderate: { name: '25% Correction', impact: -21.2, recovery_months: 14 },
      scenario_severe: { name: '40% Crash (2020-style)', impact: -35.8, recovery_months: 28 }
    },
    
    // Sector allocation
    sector_allocation: [
      { name: 'Banking', value: 55, color: '#10b981', benchmark: 35 },
      { name: 'Beverages', value: 20, color: '#3b82f6', benchmark: 15 },
      { name: 'Cement', value: 15, color: '#8b5cf6', benchmark: 20 },
      { name: 'Telecom', value: 10, color: '#f59e0b', benchmark: 30 }
    ],
    
    // Dividend projection
    dividend_projection: {
      annual_income: 485000,
      monthly_income: 40417,
      yield_percentage: 5.8,
      next_payout: '2026-03-15',
      growth_rate: 8.5
    },
    
    // AI recommendations
    recommendations: [
      {
        type: 'rebalance',
        priority: 'high',
        title: 'Reduce Banking Exposure',
        description: 'Your portfolio is 55% in banking vs benchmark 35%. Consider selling some CRDB/NMB to reduce concentration risk.',
        action: 'Sell 20% of CRDB holdings',
        potential_impact: '+8 Health Score'
      },
      {
        type: 'buy',
        priority: 'medium',
        title: 'Add Telecom Exposure',
        description: 'VODA is underweight in your portfolio. Telecom sector offers stable dividends and growth.',
        action: 'Buy VODA shares',
        potential_impact: '+5 Health Score'
      },
      {
        type: 'alert',
        priority: 'low',
        title: 'Set Price Alerts',
        description: 'TBL is near your target price. Set an alert to lock in gains.',
        action: 'Create price alert',
        potential_impact: 'Protect gains'
      }
    ],
    
    // Weekly brief
    weekly_brief: {
      period: 'Feb 8 - Feb 15, 2026',
      portfolio_change: 2.8,
      portfolio_value: 8350000,
      highlights: [
        { type: 'gain', stock: 'CRDB', change: 4.9, reason: 'Strong Q4 earnings report' },
        { type: 'gain', stock: 'NMB', change: 1.9, reason: 'Dividend announcement' },
        { type: 'loss', stock: 'TBL', change: -0.3, reason: 'Profit taking' }
      ],
      market_sentiment: 'bullish',
      upcoming_events: [
        { date: '2026-02-20', event: 'CRDB Dividend Ex-Date' },
        { date: '2026-02-25', event: 'BOT Interest Rate Decision' }
      ],
      ai_insight: 'Your portfolio outperformed the DSE All Share Index this week. The banking sector rally benefited your holdings. Consider taking partial profits on CRDB as it approaches resistance at TZS 2,900.'
    },
    
    // Goals tracking
    goals: [
      { name: 'Emergency Fund', target: 5000000, current: 3200000, deadline: '2026-06-01', on_track: true },
      { name: 'First Million', target: 10000000, current: 8350000, deadline: '2026-12-31', on_track: true }
    ]
  });

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-500';
    if (score >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return 'from-emerald-500 to-emerald-600';
    if (grade.startsWith('B')) return 'from-blue-500 to-blue-600';
    if (grade.startsWith('C')) return 'from-amber-500 to-amber-600';
    return 'from-red-500 to-red-600';
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0';
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K`;
    return amount.toLocaleString();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
            <Brain className="w-10 h-10 text-primary animate-pulse" />
          </div>
          <p className="text-muted-foreground">{t('Analyzing your portfolio...', 'Inachambua portfolio yako...')}</p>
        </div>
      </Layout>
    );
  }

  const data = intelligence || {};
  
  // Safe accessors with defaults
  const scores = data.scores || {};
  const sectorAllocation = data.sector_allocation || [];
  const recommendations = data.recommendations || [];
  const stressTest = data.stress_test || {};
  const dividendProjection = data.dividend_projection || {};
  const goals = data.goals || [];
  const weeklyBrief = data.weekly_brief || {};

  return (
    <Layout>
      <div className="page-enter space-y-6 pb-8">
        {/* Hero Header with Health Score */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border border-slate-700 p-6 md:p-8 text-white">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                  <Brain className="w-3 h-3 mr-1" />
                  {t('AI-Powered Analysis', 'Uchambuzi wa AI')}
                </Badge>
                <Badge variant="outline" className="border-white/20 text-white/70">
                  {t('Updated', 'Imesasishwa')} {data.last_updated ? new Date(data.last_updated).toLocaleDateString() : 'N/A'}
                </Badge>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                {t('Portfolio Intelligence', 'Akili ya Portfolio')}
              </h1>
              <p className="text-white/60 max-w-lg">
                {t('Your personal investment intelligence engine. Real-time health monitoring, stress testing, and AI-powered recommendations.',
                   'Injini yako ya akili ya uwekezaji. Ufuatiliaji wa afya wakati halisi, upimaji wa msongo, na mapendekezo ya AI.')}
              </p>
              <div className="mt-5 max-w-xl">
                <AskAIBar dark chips={[
                  t('Why is my health score this grade?', 'Kwa nini alama yangu ya afya iko hivi?'),
                  t('Which fund should I add to diversify?', 'Niongeze mfuko gani kwa mseto?'),
                  t('How do I rebalance step by step?', 'Nifanyeje kusawazisha hatua kwa hatua?'),
                ]} />
              </div>
            </div>

            {/* Health Score Circle */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <svg className="w-40 h-40 transform -rotate-90">
                  <circle
                    cx="80" cy="80" r="70"
                    stroke="currentColor"
                    strokeWidth="12"
                    fill="none"
                    className="text-white/10"
                  />
                  <circle
                    cx="80" cy="80" r="70"
                    stroke="url(#scoreGradient)"
                    strokeWidth="12"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${((data.health_score || 0) / 100) * 440} 440`}
                    className="transition-all duration-1000"
                  />
                  <defs>
                    <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-5xl font-bold">{data.health_score || 0}</span>
                  <span className="text-white/60 text-sm">{t('Health Score', 'Alama ya Afya')}</span>
                </div>
              </div>
              <Badge className={`mt-3 bg-gradient-to-r ${getGradeColor(data.health_grade || 'C')} text-white border-0 text-lg px-4 py-1`}>
                {t('Grade', 'Daraja')}: {data.health_grade || 'N/A'}
              </Badge>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t border-white/10">
            {Object.entries(scores).map(([key, value]) => (
              <div key={key} className="text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  {value.status === 'good' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : value.status === 'warning' ? (
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                  <span className="text-2xl font-bold">{value.score}</span>
                </div>
                <p className="text-white/60 text-xs capitalize">
                  {key.replace('_', ' ')}
                </p>
              </div>
            ))}
          </div>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" className="gap-2">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">{t('Overview', 'Muhtasari')}</span>
            </TabsTrigger>
            <TabsTrigger value="stress" className="gap-2">
              <Umbrella className="w-4 h-4" />
              <span className="hidden sm:inline">{t('Stress Test', 'Upimaji')}</span>
            </TabsTrigger>
            <TabsTrigger value="income" className="gap-2">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">{t('Income', 'Mapato')}</span>
            </TabsTrigger>
            <TabsTrigger value="brief" className="gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="hidden sm:inline">{t('AI Brief', 'Ripoti ya AI')}</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Score Breakdown */}
              <Card className="lg:col-span-2 border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    {t('Health Score Breakdown', 'Mgawanyo wa Alama ya Afya')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {Object.entries(scores).map(([key, value]) => (
                    <div key={key} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium capitalize text-foreground">
                            {key.replace('_', ' ')}
                          </span>
                          {value.status === 'good' ? (
                            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30 text-xs">
                              {t('Good', 'Nzuri')}
                            </Badge>
                          ) : value.status === 'warning' ? (
                            <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/30 text-xs">
                              {t('Needs Attention', 'Inahitaji Umakini')}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-500/10 text-red-500 border-red-500/30 text-xs">
                              {t('Critical', 'Muhimu')}
                            </Badge>
                          )}
                        </div>
                        <span className={`font-bold ${getScoreColor(value.score)}`}>
                          {value.score}/100
                        </span>
                      </div>
                      <Progress value={value.score} className="h-2" />
                      <p className="text-sm text-muted-foreground">{value.message}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Sector Allocation */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-primary" />
                    {t('Sector Allocation', 'Mgawanyo wa Sekta')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <RechartsPie>
                        <Pie
                          data={sectorAllocation}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={70}
                          dataKey="value"
                          label={({ name, value }) => `${value}%`}
                          labelLine={false}
                        >
                          {sectorAllocation.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-2 mt-4">
                    {sectorAllocation.map((sector, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: sector.color }} />
                          <span className="text-foreground">{sector.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{sector.value}%</span>
                          {sector.value > sector.benchmark + 10 && (
                            <Badge variant="outline" className="text-xs text-amber-500 border-amber-500/30">
                              {t('High', 'Juu')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* AI Recommendations */}
            <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  {t('AI Recommendations', 'Mapendekezo ya AI')}
                </CardTitle>
                <CardDescription>
                  {t('Personalized actions to improve your portfolio health', 'Hatua za kibinafsi kuboresha afya ya portfolio yako')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {recommendations.map((rec, i) => (
                  <div 
                    key={i}
                    className={`p-4 rounded-xl border ${
                      rec.priority === 'high' ? 'border-red-500/30 bg-red-500/5' :
                      rec.priority === 'medium' ? 'border-amber-500/30 bg-amber-500/5' :
                      'border-border/50 bg-muted/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {rec.type === 'rebalance' && <Scale className="w-4 h-4 text-red-500" />}
                          {rec.type === 'buy' && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                          {rec.type === 'alert' && <AlertCircle className="w-4 h-4 text-amber-500" />}
                          <span className="font-semibold text-foreground">{rec.title}</span>
                          <Badge variant="outline" className={`text-xs ${
                            rec.priority === 'high' ? 'text-red-500 border-red-500/30' :
                            rec.priority === 'medium' ? 'text-amber-500 border-amber-500/30' :
                            'text-muted-foreground'
                          }`}>
                            {rec.priority}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{rec.description}</p>
                        <div className="flex items-center gap-4">
                          <Button size="sm" variant={rec.priority === 'high' ? 'default' : 'outline'} className="rounded-full"
                            data-testid={`rec-ask-ai-${i}`}
                            onClick={() => navigate(askAIPath(t(
                              `My portfolio intelligence recommends: "${rec.title}" — ${String(rec.description || '').replace(/\.+$/, '')}. Why does this suit my portfolio, which exact investment should I choose, and how do I do it step by step?`,
                              `Akili ya portfolio yangu inapendekeza: "${rec.title}" — ${String(rec.description || '').replace(/\.+$/, '')}. Kwa nini hili linafaa portfolio yangu, nichague uwekezaji upi hasa, na nifanyeje hatua kwa hatua?`)))}>
                            <Sparkles className="w-4 h-4 mr-1" />
                            {t('Ask AI: why, which & how', 'Uliza AI: kwa nini, ipi na vipi')}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                          <span className="text-xs text-primary font-medium">{rec.potential_impact}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Stress Test Tab */}
          <TabsContent value="stress" className="space-y-6">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Umbrella className="w-5 h-5 text-primary" />
                  {t('Portfolio Stress Test', 'Upimaji wa Msongo wa Portfolio')}
                </CardTitle>
                <CardDescription>
                  {t('See how your portfolio would perform in different market conditions', 'Ona jinsi portfolio yako ingeweza kufanya katika hali tofauti za soko')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(stressTest).map(([key, scenario]) => (
                    <div 
                      key={key}
                      className={`p-6 rounded-2xl border-2 ${
                        key === 'scenario_mild' ? 'border-amber-500/30 bg-amber-500/5' :
                        key === 'scenario_moderate' ? 'border-orange-500/30 bg-orange-500/5' :
                        'border-red-500/30 bg-red-500/5'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        {key === 'scenario_mild' && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                        {key === 'scenario_moderate' && <Flame className="w-5 h-5 text-orange-500" />}
                        {key === 'scenario_severe' && <Zap className="w-5 h-5 text-red-500" />}
                        <span className="font-semibold text-foreground">{scenario.name}</span>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('Portfolio Impact', 'Athari ya Portfolio')}</p>
                          <p className="text-3xl font-bold text-red-500">
                            {scenario.impact}%
                          </p>
                          <p className="text-sm text-muted-foreground">
                            ≈ TZS {formatCurrency(Math.abs((scenario.impact || 0) / 100) * (weeklyBrief.portfolio_value || 0))} {t('loss', 'hasara')}
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">{t('Est. Recovery', 'Muda wa Kupona')}</p>
                          <p className="text-lg font-semibold text-foreground">
                            {scenario.recovery_months} {t('months', 'miezi')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 p-4 rounded-xl bg-muted/50 border border-border/50">
                  <div className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground mb-1">{t('Risk Assessment', 'Tathmini ya Hatari')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('Your portfolio has moderate resilience. In a severe crash, you could lose up to 35% but historical data suggests recovery within 2-3 years. Consider adding bonds to reduce volatility.',
                           'Portfolio yako ina ustahimilivu wa wastani. Katika ajali kali, unaweza kupoteza hadi 35% lakini data ya kihistoria inapendekeza kupona ndani ya miaka 2-3. Fikiria kuongeza bonds kupunguza mabadiliko.')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dividend Income Tab */}
          <TabsContent value="income" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                    {t('Dividend Income Projection', 'Makadirio ya Mapato ya Gawio')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-1">{t('Annual Dividend Income', 'Mapato ya Gawio ya Mwaka')}</p>
                    <p className="text-4xl font-bold text-emerald-500">
                      TZS {formatCurrency(dividendProjection.annual_income)}
                    </p>
                    <p className="text-muted-foreground mt-1">
                      ≈ TZS {formatCurrency(dividendProjection.monthly_income)}/{t('month', 'mwezi')}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl bg-background/60">
                      <p className="text-xs text-muted-foreground mb-1">{t('Dividend Yield', 'Faida ya Gawio')}</p>
                      <p className="text-2xl font-bold text-foreground">{dividendProjection.yield_percentage || 0}%</p>
                    </div>
                    <div className="p-4 rounded-xl bg-background/60">
                      <p className="text-xs text-muted-foreground mb-1">{t('Growth Rate', 'Kiwango cha Ukuaji')}</p>
                      <p className="text-2xl font-bold text-emerald-500">+{dividendProjection.growth_rate || 0}%</p>
                    </div>
                  </div>

                  <div className="p-4 rounded-xl border border-emerald-500/30">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className="text-sm font-medium text-foreground">{t('Next Dividend Date', 'Tarehe ya Gawio Ijayo')}</p>
                        <p className="text-muted-foreground">{dividendProjection.next_payout ? new Date(dividendProjection.next_payout).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Goals Tracking */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary" />
                    {t('Investment Goals', 'Malengo ya Uwekezaji')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {goals.map((goal, i) => (
                    <div key={i} className="p-4 rounded-xl border border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-foreground">{goal.name}</span>
                        {goal.on_track ? (
                          <Badge className="bg-emerald-500/20 text-emerald-500 border-0">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            {t('On Track', 'Sawa')}
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-500/20 text-amber-500 border-0">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {t('Behind', 'Nyuma')}
                          </Badge>
                        )}
                      </div>
                      <Progress value={(goal.current / goal.target) * 100} className="h-2 mb-2" />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>TZS {formatCurrency(goal.current)} / {formatCurrency(goal.target)}</span>
                        <span>{Math.round((goal.current / goal.target) * 100)}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('Deadline', 'Mwisho')}: {new Date(goal.deadline).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                  
                  <Button variant="outline" className="w-full rounded-xl" onClick={() => navigate('/goals/add')}>
                    <Target className="w-4 h-4 mr-2" />
                    {t('Add New Goal', 'Ongeza Lengo Jipya')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AI Weekly Brief Tab */}
          <TabsContent value="brief" className="space-y-6">
            <Card className="border-border/50 overflow-hidden">
              <div className="bg-gradient-to-r from-primary/20 via-primary/10 to-transparent p-6 border-b border-border/50">
                <div className="flex items-center justify-between">
                  <div>
                    <Badge className="bg-primary/20 text-primary border-0 mb-2">
                      <Sparkles className="w-3 h-3 mr-1" />
                      {t('AI-Generated Weekly Brief', 'Ripoti ya Kila Wiki ya AI')}
                    </Badge>
                    <h2 className="text-2xl font-bold text-foreground">{weeklyBrief.period || 'This Week'}</h2>
                  </div>
                  <Button variant="outline" size="sm" onClick={refreshIntelligence} disabled={refreshing} className="rounded-full">
                    <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                    {t('Refresh', 'Sasisha')}
                  </Button>
                </div>
              </div>
              <CardContent className="p-6 space-y-6">
                {/* Portfolio Performance */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('Portfolio Value', 'Thamani ya Portfolio')}</p>
                    <p className="text-2xl font-bold text-foreground">TZS {formatCurrency(weeklyBrief.portfolio_value)}</p>
                  </div>
                  <div className={`flex items-center gap-2 text-lg font-bold ${(weeklyBrief.portfolio_change || 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {(weeklyBrief.portfolio_change || 0) >= 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                    {(weeklyBrief.portfolio_change || 0) >= 0 ? '+' : ''}{weeklyBrief.portfolio_change || 0}%
                  </div>
                </div>

                {/* Weekly Highlights */}
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Flame className="w-4 h-4 text-primary" />
                    {t('This Week\'s Highlights', 'Mambo Muhimu ya Wiki Hii')}
                  </h3>
                  <div className="space-y-2">
                    {(weeklyBrief.highlights || []).map((highlight, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div className="flex items-center gap-3">
                          {highlight.type === 'gain' ? (
                            <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                              <TrendingUp className="w-4 h-4 text-emerald-500" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center">
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            </div>
                          )}
                          <div>
                            <span className="font-medium text-foreground">{highlight.stock}</span>
                            <p className="text-xs text-muted-foreground">{highlight.reason}</p>
                          </div>
                        </div>
                        <span className={`font-bold ${highlight.change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {highlight.change >= 0 ? '+' : ''}{highlight.change}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Market Sentiment */}
                <div className="flex items-center gap-4 p-4 rounded-xl border border-border/50">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    weeklyBrief.market_sentiment === 'bullish' ? 'bg-emerald-500/20' :
                    weeklyBrief.market_sentiment === 'bearish' ? 'bg-red-500/20' :
                    'bg-amber-500/20'
                  }`}>
                    {weeklyBrief.market_sentiment === 'bullish' ? (
                      <ThumbsUp className="w-6 h-6 text-emerald-500" />
                    ) : weeklyBrief.market_sentiment === 'bearish' ? (
                      <ThumbsDown className="w-6 h-6 text-red-500" />
                    ) : (
                      <Scale className="w-6 h-6 text-amber-500" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t('Market Sentiment', 'Hali ya Soko')}</p>
                    <p className={`text-lg font-bold capitalize ${
                      weeklyBrief.market_sentiment === 'bullish' ? 'text-emerald-500' :
                      weeklyBrief.market_sentiment === 'bearish' ? 'text-red-500' :
                      'text-amber-500'
                    }`}>
                      {weeklyBrief.market_sentiment || 'neutral'}
                    </p>
                  </div>
                </div>

                {/* AI Insight */}
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground mb-2">{t('AI Insight', 'Ufahamu wa AI')}</p>
                      <p className="text-muted-foreground leading-relaxed">{weeklyBrief.ai_insight || 'No insights available yet.'}</p>
                    </div>
                  </div>
                </div>

                {/* Upcoming Events */}
                <div>
                  <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" />
                    {t('Upcoming Events', 'Matukio Yanayokuja')}
                  </h3>
                  <div className="space-y-2">
                    {(weeklyBrief.upcoming_events || []).map((event, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                        <div className="w-10 text-center">
                          <p className="text-xs text-muted-foreground">{new Date(event.date).toLocaleDateString('en', { month: 'short' })}</p>
                          <p className="text-lg font-bold text-foreground">{new Date(event.date).getDate()}</p>
                        </div>
                        <p className="text-foreground">{event.event}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
